import { ApiError, api } from '../../lib/api'
import { GROUP_TYPE, USER_GROUPS_OPERATION } from '../../domain/domainCodes'
import { MAX_PAGE_SIZE, dataParams, readDataTable } from '../../lib/dataTable'
import { download } from '../../lib/download'
import { upload } from '../../lib/upload'
import { isEnglish } from '../../lib/i18n'

/**
 * Departments — `Features/Admin/GroupsManagement/Endpoint.cs`.
 *
 *   listGroups      -> GET  /api/groups          -> GroupsPageViewModel
 *   listGroupUsers  -> POST /Groups/GetUsers     { GroupId }
 *   saveGroup       -> POST /Groups/Create | /Groups/Update  { Id, ArabicName, EnglishName, TypeId }
 *   deleteGroup     -> POST /Groups/Delete       { GroupId }
 *   sendGroupEmail  -> POST /Groups/SendEmail    { GroupId, MessageTitle, MessageBody, SendTime }
 *   bulkAssign      -> POST /Groups/UpdateBulkUsersGroups   multipart: excelFile
 *   exportMembers   -> GET  /Groups/Export       ?groupId=&type=&search=
 *
 * WHY THE TABLE TAKES TWO CALLS.
 *
 * No single endpoint answers the row this screen shows. `GroupsPageViewModel.Groups` has
 * proper field names and the creation date but no member count; `/Groups/Filter` has the
 * count but no date, and positional rows (`_1` … `_6`) with the id buried in markup. So
 * the page payload is the table, and `fetchMemberCounts` fills in one column from the
 * filter. See `fetchMemberCounts` below and `API_FINDINGS.md` §20.
 *
 * Departments are a bounded set — tens or hundreds, not the tens of thousands its users
 * run to — which is what makes reading the whole list up front reasonable here and not on
 * the users screen.
 */

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** `<span style='display:none'>12</span>3` -> `12`. Parsed, never rendered. */
export function groupIdFromCell(cell) {
  const match = /<span[^>]*>(\d+)<\/span>/i.exec(cell ?? '')
  return match ? Number(match[1]) : null
}

/** `2026-08-06T…` -> `06-08-2026`, the order the table shows. */
function formatDate(iso) {
  if (!iso) return ''
  const [year, month, day] = String(iso).slice(0, 10).split('-')
  return `${day}-${month}-${year}`
}

/** 10 x 100 rows. Well past any real department list; a stop, not an expectation. */
const MAX_COUNT_PAGES = 10

/**
 * Neither endpoint has everything the table shows.
 *
 *   `/api/groups`     names, type and CREATION DATE — but no member count. `GroupDto`
 *                     simply has no count field.
 *   `/Groups/Filter`  the MEMBER COUNT (`_6` = `g.Users.Count`) — but no creation date,
 *                     and positional field names with the id hidden in markup.
 *
 * So both are read and merged by id, rather than dropping a column the screen has.
 *
 * The filter pages at `MAX_PAGE_SIZE`, so this walks it: one request, then the rest in
 * parallel off its `RecordsTotal`. Departments are a bounded set — tens or hundreds, not
 * the tens of thousands its users run to — so that is one request in practice.
 *
 * Anything this does not reach stays UNKNOWN, and the column shows `—`. A count is not
 * guessed at: "0 members" and "we could not ask" look nothing alike to whoever is about
 * to delete a department.
 */
async function fetchMemberCounts(signal) {
  const readPage = async (page) =>
    readDataTable(
      await api.post(
        '/Groups/Filter',
        dataParams({ page, pageSize: MAX_PAGE_SIZE, columns: ['name'] }),
        { signal },
      ),
    )

  try {
    const first = await readPage(1)
    const pages = Math.min(Math.ceil(first.total / MAX_PAGE_SIZE), MAX_COUNT_PAGES)
    const rest = await Promise.all(
      Array.from({ length: Math.max(pages - 1, 0) }, (_, index) => readPage(index + 2)),
    )

    const counts = new Map()
    for (const { rows } of [first, ...rest]) {
      for (const row of rows) {
        const id = groupIdFromCell(row._1)
        if (id != null) counts.set(id, Number(row._6) || 0)
      }
    }
    return counts
  } catch {
    /* The list is still worth showing without its counts. */
    return new Map()
  }
}

export async function listGroups(signal) {
  const [dto, counts] = await Promise.all([
    api.get('/api/groups', { signal }).then((value) => value ?? {}),
    fetchMemberCounts(signal),
  ])

  const types = new Map(
    (dto.GroupsTypes ?? []).map((type) => [
      type.GroupTypeId,
      label(type.ArabicName, type.EnglishName),
    ]),
  )

  const rows = (dto.Groups ?? []).map((group) => ({
    id: group.GroupId,
    name: label(group.ArabicName, group.EnglishName),
    arabicName: group.ArabicName ?? '',
    englishName: group.EnglishName ?? '',
    typeId: group.TypeId,
    typeName: types.get(group.TypeId) ?? group.TypeName ?? '',
    createdOn: group.CreationDate ?? null,
    // The table renders this one; keeping both means neither side has to format.
    createdOnText: formatDate(group.CreationDate),
    /* Where the department came from. A synced department cannot be renamed here — the
       next synchronisation would put the old name back. */
    source: group.Source,
    integrationId: group.IntegrationId ?? '',
    /** A number, or `null` for "not known" — never a stand-in zero. See above. */
    usersCount: counts.get(group.GroupId) ?? null,
  }))

  return {
    rows,
    total: rows.length,
    types: [...types].map(([id, name]) => ({ value: id, label: name })),
    dataSources: (dto.DataSources ?? []).map((source) => ({
      id: source.Id,
      name: label(source.ArabicName, source.EnglishName),
      type: source.Type ?? '',
    })),
    /* Whether "clone from the directory" can work at all, and whether emptied synced
       departments are removed automatically. */
    hasDepartmentsDataSource: Boolean(dto.DepartmentsIntegrationDataSourceExist),
    autoDeleteEmptySystemGroups: Boolean(dto.IsAutoDeleteEmptySystemGroups),
  }
}

/** `<span style='display:none'>ali</span>ali` -> `ali`, not `aliali`. */
function userNameFromLink(cell) {
  const match = /<span[^>]*>([^<]*)<\/span>/i.exec(cell ?? '')
  return (match ? match[1] : String(cell ?? '').replace(/<[^>]*>/g, '')).trim()
}

/**
 * The members behind the `قائمة المستخدمين` row action.
 *
 * `/Groups/GetUsers` binds `GetUsersRequest(int GroupId, DataTablesRequest? DataParams)` —
 * the params NESTED, unlike `/Groups/Filter` next door, which binds a bare
 * `DataTablesRequest`. Sending them flat is not an error: `DataParams` comes through null
 * and the handler quietly falls back to the first ten rows, unsearched and unsorted.
 */
export async function listGroupUsers(groupId, { page = 1, pageSize = 10, search = '', sort, signal } = {}) {
  const response = await api.post(
    '/Groups/GetUsers',
    {
      GroupId: Number(groupId),
      DataParams: dataParams({ page, pageSize, search, sort, columns: ['name', 'userName', 'email'] }),
    },
    { signal },
  )

  const { rows, total } = readDataTable(response)

  return {
    rows: rows.map((row) => {
      const userName = userNameFromLink(row.Link)
      return {
        id: userName,
        userName,
        name: label(row.ArabicName, row.EnglishName) || userName,
        email: row.Email ?? '',
        status: (row.Status ?? []).map((badge) => ({ label: badge.Status ?? '', color: badge.Color ?? 'dark' })),
      }
    }),
    total,
  }
}

/**
 * One outcome shape for every action on this screen: `{ ok, message, code, id, dataSet }`.
 *
 * `dataSet` is how `UsersGroups` reports a PARTIAL result — the usernames it could not
 * find. It rides on a `Result: false` body, which `api.js` turns into an `ApiError`, so it
 * and `code` are read back off `error.data` rather than being lost with it. `code` is what
 * tells a partial result from a real failure; see `asPartial`.
 */
async function act(path, body) {
  try {
    const data = await api.post(path, body)
    return {
      ok: true,
      message: data?.Message ?? '',
      code: data?.Code ?? USER_GROUPS_OPERATION.SUCCESS,
      id: data?.GroupId,
      dataSet: data?.DataSet ?? [],
    }
  } catch (error) {
    if (error instanceof ApiError)
      return {
        ok: false,
        message: error.message,
        code: error.data?.Code ?? null,
        dataSet: error.data?.DataSet ?? [],
      }
    throw error
  }
}

/** Create and update take the same body; only the route and a non-zero `Id` differ. */
export function saveGroup(values, isUpdate) {
  return act(isUpdate ? '/Groups/Update' : '/Groups/Create', {
    Id: isUpdate ? Number(values.id) : 0,
    ArabicName: values.arabicName ?? '',
    EnglishName: values.englishName ?? '',
    TypeId: Number(values.typeId ?? GROUP_TYPE.DEPARTMENTS),
  })
}

export const deleteGroup = (groupId) => act('/Groups/Delete', { GroupId: Number(groupId) })

/** Removes every synced department left with no members. */
export const deleteEmptySyncDepartments = () => act('/Groups/DeleteEmptySyncDepartments', {})

export const cloneDepartments = (activeDirectoryId) =>
  act('/Groups/Clone', { ActiveDirectoryId: Number(activeDirectoryId) })

/**
 * @param {Date|string} [sendTime] when to send. The command takes a `DateTime`, so an
 *        omitted value becomes "now" rather than an empty string the binder would reject.
 */
export const sendGroupEmail = ({ groupId, title, body, sendTime }) =>
  act('/Groups/SendEmail', {
    GroupId: Number(groupId),
    MessageTitle: title ?? '',
    MessageBody: body ?? '',
    SendTime: sendTime ?? new Date().toISOString(),
  })

/**
 * Bulk department assignment from a spreadsheet.
 *
 * Answers JSON rather than the `finishUpload` callback — `UpdateBulkUsersGroups` returns
 * `{ Result, Message, DataSet2 }`, where `DataSet2` lists the rows it could not take.
 * Those are surfaced, because "12 of 300 rows failed" is a different outcome from none.
 */
export async function bulkAssignUsers({ file }) {
  if (!file) return { ok: false, message: '', rejected: [] }

  const form = new FormData()
  form.append('excelFile', file)

  try {
    const data = await upload('/Groups/UpdateBulkUsersGroups', form)
    return {
      ok: true,
      message: data?.Message ?? '',
      rejected: (data?.DataSet2 ?? []).map((row) => ({
        row: row.RowCount ?? '',
        message: row.RowMessage ?? '',
        username: row.Username ?? '',
        group: row.Group ?? '',
      })),
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, rejected: [] }
    throw error
  }
}

/* ------------------------------------------------------------------ *
 * Membership — `Features/Admin/UsersGroups/Endpoint.cs`.
 *
 * A slice of its own with no page route: the legacy `/UsersGroups` screen listed users
 * against their private groups, and React folded that into this one (`/usersgroups`
 * redirects to `/groups`, and the members drawer is where membership is edited).
 *
 * All three take usernames, not ids — `Profiles.UserName` is the key throughout.
 * ------------------------------------------------------------------ */

/**
 * `AddUsersToGroupHandler` ADDS EVERY USER IT FOUND AND SAVES, and only then reports the
 * names it could not find — as `Result: false`, `Code: UnknownUser`. So that body means
 * "partly done", not "nothing happened", and taking it at face value left the users that
 * WERE added invisible: the drawer stayed on the import tab and never reloaded. See
 * `API_FINDINGS.md`.
 *
 * Here it becomes a success carrying a report, which is what makes the drawer reset and
 * refresh on its own, and what gives the page something to show in the unknown-users modal.
 *
 * Gated on the CODE, never on `dataSet.length`: `InvalidInput` (a file with empty rows)
 * saves nothing and has to stay a failure.
 */
function asPartial(outcome) {
  if (outcome.code !== USER_GROUPS_OPERATION.UNKNOWN_USER) return outcome

  const unknownUsers = dedupe(outcome.dataSet)
  if (unknownUsers.length === 0) return outcome

  return { ...outcome, ok: true, partial: true, unknownUsers }
}

/**
 * Case-insensitively, first spelling wins.
 *
 * The server does not deduplicate, so a spreadsheet listing the same missing name in three
 * rows reports it three times — and the table count and the exported file both have to say
 * what an administrator has to go and fix, which is one name.
 */
function dedupe(names) {
  const seen = new Set()
  const out = []

  for (const name of names ?? []) {
    const text = String(name ?? '').trim()
    if (!text) continue

    const key = text.toLocaleLowerCase()
    if (seen.has(key)) continue

    seen.add(key)
    out.push(text)
  }

  return out
}

/** @param {string[]} users usernames */
export const addUsersToGroup = ({ users, groupId }) =>
  act('/UsersGroups/AddUsersToGroup', { Users: users, UserGroup: Number(groupId) }).then(asPartial)

/** @param {string[]} users usernames */
export const removeUsersFromGroup = ({ users, groupId }) =>
  act('/UsersGroups/RemoveUsersFromGroup', { Users: users, UserGroup: Number(groupId) })

/**
 * The same add, from a spreadsheet: first sheet, header row skipped, column A is the
 * username. The server reads the file and hands the names to the call above, so a file
 * naming users that do not exist comes back the same way — `Result: false` with the
 * unknown names in `DataSet`, reinterpreted by `asPartial`.
 */
export async function addUsersFromExcel({ file, groupId }) {
  if (!file) return { ok: false, message: '', code: null, dataSet: [] }

  const form = new FormData()
  form.append('excelFile', file)
  form.append('userGroup', String(groupId))

  try {
    const data = await upload('/UsersGroups/AddUsersOnExcelToGroup', form)
    return asPartial({
      ok: true,
      message: data?.Message ?? '',
      code: data?.Code ?? USER_GROUPS_OPERATION.SUCCESS,
      dataSet: data?.DataSet ?? [],
    })
  } catch (error) {
    if (error instanceof ApiError)
      return asPartial({
        ok: false,
        message: error.message,
        code: error.data?.Code ?? null,
        dataSet: error.data?.DataSet ?? [],
      })
    throw error
  }
}

/** @param {'excel'|'pdf'|'doc'} format the server matches these case-insensitively. */
export async function exportGroupUsers({ groupId, format, search = '' }) {
  const query = new URLSearchParams({ groupId: String(groupId), type: format, search })

  try {
    const saved = await download(`/Groups/Export?${query}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
